import {BinaryHeap} from "../binary-heap";
import {
  PointCloud,
  RawPointCloud,
  RawPointCloudNode,
  EncompassPointCloud,
  E57PointCloud
} from ".";
import {assignPackedData, PointCloudMaterialParams} from "../rendering";
import {Viewer} from "../main";
import {
  boundsToMapLayer,
  closestPowerOfTwo,
  getProjFactor,
  mergeBoundingBoxes
} from "../utilities";
import { pointInPolygon } from "../utilities";
import {
  BufferGeometry,
  Frustum,
  MathUtils,
  Matrix4,
  Points,
  Scene,
  Vector2,
  Vector3
} from "three";
import { Vector as VectorLayer } from "ol/layer";
import { WorkerQueue } from "../worker";
import { isStaticSite } from "../../../electron-modules";

export class PointCloudManager {
    public viewer: Viewer;
    public scene: Scene;
    private layer: VectorLayer;
    public materialDefaults: PointCloudMaterialParams;
    public clouds: (RawPointCloud | EncompassPointCloud)[] = [];
    public workerQueue: WorkerQueue;
    public visiblePointsTarget = 0;
    private maxloadedToGPUPerFrame = 3;
    private maxNodeAsyncLoad = 4;
    private maxRawAsyncLoad = 2;
    private maxDecodeWorkers = 6;
    private exeLaunchTimeout = 1000;
    private lastExeLaunched = performance.now();
    public numLoadedPoints = 0;
    public numVisiblePoints = 0;
    public numSceneNodesVisible = 0;
    private fovDifferencePercent = 0.5;
    private minPixelPercent = 0.20;
    private boundingBoxVisibility = false;
    public transparency = 1.0;
    public rawJsonResponse = [];

    constructor(parent: Viewer) {
      this.viewer = parent;

      // Material defaults
      this.materialDefaults = {
        minSize: 1.0,
        maxSize: 50.0,
        colorMap: "heat-map",
        colorType: 2,
        distance: this.viewer.far,
        size: 1.0,
        dynamicSize: true,
        circularPoints: true,
        classification: {},
        heightClamp: new Vector2(0, 1),
        intensityClamp: new Vector2(0, 1),
      };

      this.scene = new Scene();

      // Work queue doesn't need to exist for static site
      if (isStaticSite) return;

      const workerURL = "workers/pointcloud-decoder-buffer.js";
      this.workerQueue = new WorkerQueue(workerURL,
        this.maxDecodeWorkers, true);
    }

    get cameraList() {
      return this.viewer.cameraList;
    }

    get minimap() {
      return this.viewer.minimap;
    }

    get controls() {
      return this.viewer.controls;
    }

    get uniqueClassifications(): number[] {
      let uniqueClassifications = new Set();
      this.clouds.forEach(cloud => {
        cloud.uniqueClassifications.forEach((item: number) => {
          uniqueClassifications.add(item);
        });
      });
      return Array.from(uniqueClassifications) as number[];
    }

    get combinedBoundingBox() {
      let boundingBox = null;
      this.clouds.forEach(cloud => {
        if (!cloud.visible) {
          return;
        }

        boundingBox = mergeBoundingBoxes(boundingBox,
          cloud.tightBoundingBox);
      });
      return boundingBox;
    }

    get isEmpty() {
      return this.clouds.length === 0;
    }

    get rawPointClouds() : Array<RawPointCloud> {
      let clouds = this.clouds.filter(x => x instanceof RawPointCloud);
      return clouds as Array<RawPointCloud>;
    }

    getByID(id){
      const pointCloud = this.clouds.filter(pointCloud => pointCloud.identifier === id);
      return pointCloud.length === 1 ? pointCloud[0] : null;
    }

    doesCloudExist(identifier: string): boolean {
      return this.clouds.filter(cloud => cloud.identifier === identifier).length > 0;
    }

    addPointCloud(cloud: RawPointCloud | EncompassPointCloud | E57PointCloud) {
      this.clouds.push(cloud);
      this.updateMeasureList();
    }

    deletePointCloud(identifier) {
      const clouds = new Set(this.clouds);
      const toRemove = this.clouds.filter(cloud => cloud.identifier === identifier);

      toRemove.forEach(cloud => {
        // console.log(`Remove pointcloud [id = ${cloud.identifier}]`);
        cloud.dispose();
        clouds.delete(cloud);
      });

      this.clouds = [...clouds];
      this.updateMinimapExtent();
      this.updateMeasureList();
    }

    updateMeasureList() {
      const measurements = this.controls.measurements;
      measurements.refreshMeasurementList();
    }

    toggleVisibility(identifier, state) {
      this.clouds
        .filter(cloud => cloud.identifier === identifier)
        .forEach(cloud => {
          cloud.setVisibility(state);
        });
    }

    toggleBoundingBox() {
      this.boundingBoxVisibility = !this.boundingBoxVisibility;
      console.log("Bounding box visible: ", this.boundingBoxVisibility);
    }

    hideDescendants(cloud) {
      if (!cloud.root) {
        return;
      }

      let stack = [cloud.root];

      while (stack.length > 0) {
        let object = stack.shift();
        if (!object.visible) {
          continue;
        }

        object.setVisibility(false, false);

        object.children.forEach(child => {
          stack.push(child);
        });
      }
    }

    updateClamping(defaultKey, materialKey, data) {
      if (data.max <= data.min) {
        return;
      }

      let value = new Vector2(data.min, data.max);
      this.materialDefaults[defaultKey] = value;
      this.clouds.forEach(cloud => {
        cloud.materials.forEach(material => {
          material[materialKey] = value;
        });
      });
    }

    setClassification(classification, visible) {
      this.materialDefaults.classification[classification] = visible;

      this.clouds.forEach(cloud => {
        cloud.materials.forEach(material => {
          let uniforms = material.uniforms;
          let texture = uniforms.classification_visible.value;
          let data = texture.image.data;

          // Set visible bit to min or max value
          data[classification * 4 + 3] = visible ? 255 : 0;
          texture.needsUpdate = true;
        });
      });
    }

    updateMinimapExtent() {
      const map = this.minimap.map;
      if (this.layer) {
        map.removeLayer(this.layer);
      }

      let boundingBox = this.combinedBoundingBox;
      if (!boundingBox) {
        return;
      }

      // Aerial view layer
      const mapLayer = boundsToMapLayer(boundingBox);
      this.layer = mapLayer;
      map.addLayer(this.layer);
    }

    updateVisibility(frustum, cloud: PointCloud) {
      if (this.cameraList.waitForCameras) {
        return;
      }

      let priorityQueue = new BinaryHeap(x => 1 / x.weight);
      priorityQueue.push({
        node: cloud.root,
        weight: Number.MAX_VALUE
      });

      let unloadedGeometry = [];
      let numLoadedPoints = 0;
      let loadedToGPUThisFrame = 0;

      let maxNodeDistance = cloud.materialScene.distance;
      let controls = this.viewer.controls;
      let measurePoint = controls.getCameraTarget();

      while (priorityQueue.size() > 0) {
        let element = priorityQueue.pop();
        let node = element.node;

        // Raw pointclouds dont require certain steps
        let isRawPointCloud = node instanceof RawPointCloudNode;

        let newPointsTarget = numLoadedPoints;
        if (node.numPoints !== null) {
          newPointsTarget += node.numPoints;
        }

        // Ignore nodes based on our view frustrum
        let visible = frustum.intersectsSphere(node.boundingSphere);

        if (!isRawPointCloud) {
          // Make sure we dont exceeed our points limit
          visible = visible && (newPointsTarget < this.visiblePointsTarget);
        }

        if (!visible) {
          continue;
        }

        // Ignore nodes based on distance slider
        let bounds2d = new Vector2().copy(node.center);
        let camera2d = new Vector2().copy(measurePoint);
        let nodeDistance = bounds2d.distanceTo(camera2d);
        if (nodeDistance > (node.radius + maxNodeDistance)) {
          continue;
        }

        if (node.isGeometryNode() && (!node.parent || node.parent.isTreeNode())) {
          if (node.isLoaded() && loadedToGPUThisFrame < this.maxloadedToGPUPerFrame) {
            node.addToScene(node);
            loadedToGPUThisFrame++;
          } else {
            unloadedGeometry.push(node);
          }
        }

        if (node.isTreeNode()) {
          node.setVisibility(true, this.boundingBoxVisibility);
          numLoadedPoints += node.numPoints;
        }

        let cameraPosition = this.viewer.camera.position;
        node.children.forEach(child => {
          let childDistance = child.center.distanceTo(cameraPosition);
          let childRadius = child.radius;

          // Modify the fov range so we dont load too many points while zooming
          let maxFov = this.viewer.controls.mouseControls.maxFOV;
          let fov = this.viewer.camera.fov;
          fov = maxFov - (maxFov - fov) * this.fovDifferencePercent;
          fov = MathUtils.degToRad(fov);

          const screenHeight = this.viewer.height;
          let projFactor = getProjFactor(fov, childDistance, screenHeight);
          let screenPixelRadius = childRadius * projFactor;

          let insideSphere = childDistance < childRadius;

          if (!isRawPointCloud) {
            // Pixel size based filtering
            let minPixelSize = this.viewer.height * this.minPixelPercent;
            let validPixelSize = screenPixelRadius >= minPixelSize;
            if (!validPixelSize) {
              return;
            }
          }

          let weight = screenPixelRadius;
          if (insideSphere) {
            weight = Number.MAX_VALUE;
          }

          priorityQueue.push({
            node: child,
            weight: weight
          });
        });
      }

      let maxLoad = Math.min(this.maxNodeAsyncLoad, unloadedGeometry.length);
      for (let i = 0; i < maxLoad; i++) {
        unloadedGeometry[i].load();
      }

      this.numLoadedPoints += numLoadedPoints;
    }

    updateMaterialValues(cloud: PointCloud, material) {
      let controls = this.viewer.controls;
      let camera = this.viewer.camera;
      let cameraPosition = controls.getCameraTarget();

      material.dynamic_cloud = !(cloud instanceof RawPointCloud);
      material.origin = cameraPosition;
      material.screenHeight = this.viewer.height;
      material.screenwidth = this.viewer.width;
      material.fov = MathUtils.degToRad(camera.fov);
      material.time_stamp = performance.now();

      if (cloud instanceof EncompassPointCloud) {
        material.octree_spacing = cloud.spacing;
        material.octree_size = parseFloat(cloud.root.size);
        material.octree_corner = new Vector3()
          .add(cloud.root.center)
          .subScalar(cloud.root.size / 2.0);
      }

      let intensityRange = cloud.intensityRange;
      if (intensityRange) {
        let intensity = new Vector2(intensityRange.min,
          intensityRange.max);
        material.intensity_range = intensity;
      }

      let maxColor = cloud.maxColor;
      if (maxColor) {
        let maxValue = closestPowerOfTwo(maxColor);
        material.rgb_max = maxValue;
      }

      let tightBoundingBox = this.combinedBoundingBox;
      if (tightBoundingBox) {
        let height = new Vector2(tightBoundingBox.min.z,
          tightBoundingBox.max.z);
        material.height_range = height;
      }
    }

    updateCloudSampling() {
      let percent = this.visiblePointsTarget/this.numLoadedPoints;
      percent = Math.min(1.0, percent);

      this.clouds.forEach(cloud => {
        if (!(cloud instanceof RawPointCloud)) {
          // Only applicable to raw pointclouds
          return;
        }

        if (!cloud.visible) {
          return;
        }

        if (!cloud.root) {
          return;
        }

        cloud.root.children.forEach(child => {
          if (!child.geometry) {
            return;
          }

          let numPoints = child.numPoints;
          let sampledPoints = Math.floor(numPoints*percent);
          child.geometry.setDrawRange(0, sampledPoints);
        });
      });
    }

    calculatePointsVisible() {
      let visibleNodes = this.scene.children
        .filter(x => x instanceof Points)
        .filter(x => x.visible) as Points[];

      this.numVisiblePoints = 0;
      this.numSceneNodesVisible = visibleNodes.length;

      visibleNodes.forEach(node => {
        let geometry = node.geometry as BufferGeometry;
        let numPoints = geometry.attributes.position.count;
        let numVisible = Math.min(numPoints, geometry.drawRange.count);
        this.numVisiblePoints += numVisible;
      });
    }

    manageNodeCache(cloud: EncompassPointCloud) {
      let stack = [cloud.root];

      let totalVisibleNodes = 0;
      let unusedNodes = [];

      while (stack.length > 0) {
        let object = stack.shift();

        if (object.visible) {
          totalVisibleNodes++;
        } else if (object.loaded) {
          unusedNodes.push(object);
        } else {
          continue;
        }

        object.children.forEach(child => {
          stack.push(child);
        });
      }

      let totalUnusedNodes = unusedNodes.length;
      let totalNumberNodes = totalVisibleNodes + totalUnusedNodes;
      let maxNodesAllowed = cloud.nodeCacheSize;

      if (totalNumberNodes < maxNodesAllowed) {
        return;
      }

      // console.log(`${totalNumberNodes}/${maxNodesAllowed} Nodes loaded. Visible:  ${totalVisibleNodes}, Cached: ${totalUnusedNodes}`);

      // We delete from our list of unused nodes plus an extra percentage of the total
      let numberToDelete = totalNumberNodes - maxNodesAllowed + maxNodesAllowed * 0.20;
      numberToDelete = Math.min(numberToDelete, totalUnusedNodes);
      if (numberToDelete === 0) {
        return;
      }

      // console.log(`Dispose ${numberToDelete} cached nodes`);

      // Delete unused nodes, starting at the highest detail level we have
      unusedNodes.reverse();
      unusedNodes.forEach((node, index) => {
        if (index >= numberToDelete) {
          return;
        }

        node.dispose();
      });
    }

    updateVisibilityTexture(cloud: EncompassPointCloud, material) {
      // Static sizing, exit the function
      if (!material.dynamic_size) {
        return;
      }

      let visibleNodes = cloud.getVisibleNodes();
      let texture = material.visibleNodesTexture;
      let data = texture.image.data;

      visibleNodes.forEach((node, nodeIndex) => {
        data[nodeIndex * 3 + 0] = 0; // indicate which of the 8 children are visible
        data[nodeIndex * 3 + 1] = 0; // relative offset to the node’s first child.
        data[nodeIndex * 3 + 2] = 0; // empty

        // The children nodes that are visible from our original array
        let children = visibleNodes.filter(child => {
          return child.parent === node;
        });

        children.forEach((child, childIndex) => {
          let index = parseInt(child.name.substr(-1));

          // Set the bit that represents this node to true
          data[nodeIndex * 3 + 0] += Math.pow(2, index);

          if (childIndex === 0) {
            // Save the offset to our first child node
            let vArrayIndex = visibleNodes.indexOf(child);
            data[nodeIndex * 3 + 1] = vArrayIndex - nodeIndex;
          }
        });
      });

      texture.needsUpdate = true;
    }

    updateClipBoxTexture(material) {
      let texture = material.clipBoxTexture;

      let clipBoxGeometry = this.controls.pointMarkup.getMarkupData(false);
      let numClipBoxes = clipBoxGeometry.length;

      clipBoxGeometry.forEach((data, index) => {
        if (index >= texture.image.width) {
          return;
        }

        const {position, rotation, windowSize, focalLength,
          boundingBox, newClassification, pointsHidden, reclassifyPoints,
          ignoreGround, highlight, ignorePoint, isLastBlock} = data;

        // bottom left corner
        assignPackedData(texture, index, 0, boundingBox[0]);
        assignPackedData(texture, index, 1, boundingBox[1]);

        // top right corner
        assignPackedData(texture, index, 2, boundingBox[2]);
        assignPackedData(texture, index, 3, boundingBox[3]);

        // focal length
        assignPackedData(texture, index, 4, focalLength);

        // window size
        assignPackedData(texture, index, 5, windowSize.x);
        assignPackedData(texture, index, 6, windowSize.y);

        // position
        assignPackedData(texture, index, 7, position.x);
        assignPackedData(texture, index, 8, position.y);
        assignPackedData(texture, index, 9, position.z);

        // rotation
        assignPackedData(texture, index, 10, rotation.x);
        assignPackedData(texture, index, 11, rotation.y);
        assignPackedData(texture, index, 12, rotation.z);

        // points hidden
        assignPackedData(texture, index, 13, pointsHidden);

        // reclassify points
        assignPackedData(texture, index, 14, reclassifyPoints);

        // new classification
        assignPackedData(texture, index, 15, newClassification);

        // ignore ground classified data
        assignPackedData(texture, index, 16, ignoreGround);

        // highlight selection
        assignPackedData(texture, index, 17, highlight);

        // unhide selection (for current group only)
        assignPackedData(texture, index, 18, ignorePoint);

        // group id (used for unhiding previous operations)
        assignPackedData(texture, index, 19, isLastBlock);
      });

      if (numClipBoxes > 0) {
        texture.needsUpdate = true;
      }

      material.updateNumClippingBoxes(numClipBoxes);
    }

    updateAreaTriangleTexture(material) {
      let texture = material.areaTriangleTexture;

      const measurements = this.controls.measurements;
      let areaGeometry = measurements.measurementGroup.getAreaGeometry();

      let numAreaTriangles = 0;
      areaGeometry.forEach(data => {
        data.indices.forEach(triangleIndex => {
          let index = numAreaTriangles;
          if (index >= texture.image.width) {
            return;
          }

          // volume status
          assignPackedData(texture, index, 0, data.volumeStatus);

          // vertex #1
          let vertex1 = data.vertices[triangleIndex[0]];
          assignPackedData(texture, index, 1, vertex1.x);
          assignPackedData(texture, index, 2, vertex1.y);
          assignPackedData(texture, index, 3, vertex1.z);

          // vertex #2
          let vertex2 = data.vertices[triangleIndex[1]];
          assignPackedData(texture, index, 4, vertex2.x);
          assignPackedData(texture, index, 5, vertex2.y);
          assignPackedData(texture, index, 6, vertex2.z);

          // vertex #3
          let vertex3 = data.vertices[triangleIndex[2]];
          assignPackedData(texture, index, 7, vertex3.x);
          assignPackedData(texture, index, 8, vertex3.y);
          assignPackedData(texture, index, 9, vertex3.z);

          numAreaTriangles += 1;
        });
      });

      if (numAreaTriangles > 0) {
        texture.needsUpdate = true;
      }

      material.updateNumAreaTriangles(numAreaTriangles);
    }

    pushToWorkerQueue(data, callback) {
      this.workerQueue.push(data, callback, [data.buffer]);
    }

    loadAvailableLasData() {
      let numLoading = 0;
      let cloudsToParse = [];
      let cloudsToExecute = [];

      // Execute next available in worker queue
      this.workerQueue.process();

      this.rawPointClouds.forEach(cloud => {
        if ((!cloud.canReadData) || (!cloud.visible) || (cloud.loaded)) {
          return;
        }

        if (numLoading >= this.maxRawAsyncLoad) {
          return;
        }

        if (cloud.generatingChunks) {
          cloudsToParse.push(cloud);
        } else {
          let timeNow = performance.now();
          let timeSinceLaunch = timeNow - this.lastExeLaunched;
          if (timeSinceLaunch > this.exeLaunchTimeout) {
            this.lastExeLaunched = timeNow;
            cloudsToExecute.push(cloud);
          }
        }

        numLoading += 1;
      });

      if (cloudsToExecute.length > 0) {
        let cloud = cloudsToExecute.shift();
        cloud.launchExecutable();
        return;
      }

      for (let cloud of cloudsToParse) {
        cloud.parseAvailableChunks();
      }
    }

    pointsInRadius(position, size) {
      var points = [];

      this.clouds.forEach(cloud => {
        // Grab valid nodes
        let nodes = cloud.getVisibleNodes(node => {
          let dx = Math.abs(node.center.x - position.x);
          let dy = Math.abs(node.center.y - position.y);

          return !((dx > node.size) || (dy > node.size));
        });

        // Grab valid points
        nodes.forEach(node => {
          let geometry = node.pointsScene.geometry as BufferGeometry;
          let positions = geometry.attributes.position;

          for (var i = 0; i < positions.count; i++) {
            let x = positions.getX(i) + node.pointsScene.position.x;
            let y = positions.getY(i) + node.pointsScene.position.y;
            let z = positions.getZ(i) + node.pointsScene.position.z;

            let dx = Math.abs(x - position.x);
            let dy = Math.abs(y - position.y);

            if ((dx > size) || (dy > size)) {
              continue;
            }

            let point = new Vector3(x, y, z);
            points.push(point);
          }
        });
      });

      return points;
    }

    pointsInPolygon(points, vertices) {
      let pointsInPolygon = [];

      points.forEach(point => {
        if (pointInPolygon(point, vertices)) {
          pointsInPolygon.push(point);
        }
      });

      return pointsInPolygon;
    }

    getSampledPoints(points, highest, keyfunc) {
      let samples = {};
      points.forEach(point => {
        let key = keyfunc(point);
        if (!(key in samples)) {
          samples[key] = [];
        }

        samples[key].push(point);
      });

      let dtmPoints = [];
      let keys = Object.keys(samples);
      keys.forEach(key => {
        let data = samples[key];
        data.sort((a, b) => (a.z > b.z) ? 1 : -1);
        if (highest) {
          data.reverse();
        }
        dtmPoints.push(data[0]);
      });

      console.log(`Sampled points: ${points.length} => ${dtmPoints.length}`);
      return dtmPoints;
    }

    update() {
      const camera = this.viewer.camera;

      // Set up our frustum
      let frustum = new Frustum();
      let cameraViewProjectionMatrix = new Matrix4();

      camera.updateProjectionMatrix();
      camera.updateMatrixWorld(true);
      camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
      cameraViewProjectionMatrix.multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse
      );

      frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);

      this.numLoadedPoints = 0;

      if (!isStaticSite) {
        // Keep loading raw pointcloud data
        this.loadAvailableLasData();
      }

      // Iterate each pointcloud and figure out what to show
      this.clouds.forEach(cloud => {
        this.hideDescendants(cloud);
        if (!cloud.visible) {
          return;
        }

        if (!cloud.root) {
          return;
        }

        this.updateVisibility(frustum, cloud);

        if (cloud instanceof EncompassPointCloud) {
          this.manageNodeCache(cloud);
        }

        cloud.materials.forEach(material => {
          if (!material) {
            return;
          }

          this.updateMaterialValues(cloud, material);

          if (!material.gpuPicker) {
            this.updateAreaTriangleTexture(material);
            this.updateClipBoxTexture(material);
          }

          if (cloud instanceof EncompassPointCloud) {
            this.updateVisibilityTexture(cloud, material);
          }
        });
      });

      // Sample raw pointclouds based on total points loaded
      this.updateCloudSampling();

      this.calculatePointsVisible();
    }
}